import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { InputNode, MessagingTopology, OutputNode, OutputTargetNode } from '@internal/open-catalog';
import { Button, createStyles, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemText, makeStyles, TextField, Theme } from '@material-ui/core';
import { Autocomplete } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import useAsync from 'react-use/esm/useAsync';

export interface OutputTargetDialogProps {
  open: boolean;
  environment: string;
  source: OutputNode;
  onClose: (newDestination?: OutputTargetNode) => void;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    content: {
      paddingTop: 0,
      paddingBottom: 0
    },
    infoList: {
      '& > li': {
        padding: 0
      },
      '& > li > div': {
        maxWidth: '50%',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1)
      },
      '& > li > div:first-child': {
        paddingLeft: 0
      },
      '& > li > div:last-child': {
        paddingRight: 0
      }
    },
    target: {
      overflow: 'unset',
      '& div[class|="MuiFormControl-root"]': {
        width: '100%'
      }
    }
  })
);

export function OutputTargetDialog(props: OutputTargetDialogProps) {

  const classes = useStyles();
  const { onClose, open, environment, source } = props;

  const catalogApi = useApi(catalogApiRef);
  const [target, setTarget] = useState<OutputTargetNode>();
  const inputRef = useRef<HTMLInputElement>();

  const emptyState = () => {
    setTarget(undefined);
  };

  const cancel = () => {
    onClose();
    emptyState();
  };

  const submit = () => {
    onClose(target);
    emptyState();
  };

  const { value: targets } = useAsync(async () => {
    const response = await catalogApi.getEntities({
      filter: {
        kind: 'Resource',
        ['spec.type']: 'Messaging',
        ['spec.environment']: environment
      }
    });
    return response.items.filter(mr => mr.spec)
      .map(mr => mr.spec?.topology as MessagingTopology)
      .filter(topology => topology.output?.name !== source.name)
      .filter(topology => topology.input)
      .map(topology => topology.input!)
      .sort((a, b) => a.name.localeCompare(b.name)) as InputNode[]
  }, [catalogApi, environment, source]);

  const handleDestinationChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: InputNode | null,
  ) => {
    const selected = targets?.find(t => t.name === (value ? value.name : ''));

    if (selected) {
      setTarget({
        name: selected.name,
        type: selected.type,
        arguments: selected.arguments,
        bindings: [],
      });
    } else {
      setTarget(undefined);
    }
  };

  useEffect(() => {
    setTimeout(() => inputRef?.current?.focus(), 100)
  }, [open, inputRef]);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>New output target</DialogTitle>
      <DialogContent className={classes.content}>
        <List dense className={classes.infoList}>
          <ListItem>
            <ListItemText
              primary="Source"
              secondary={source.name}
              title={source.name}
            />
            <ListItemText className={classes.target}>
              <Autocomplete
                options={targets ?? []}
                getOptionLabel={(option) => option.name}
                onChange={handleDestinationChange}
                renderInput={(params) => (
                  <TextField required
                    {...params}
                    label="Destination"
                    variant="outlined"
                    size="small"
                    placeholder={target ? target.name : 'Select destination...'}
                  />
                )}
                noOptionsText="No results found"
              />
            </ListItemText>
          </ListItem>
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel} color="primary">
          Cancel
        </Button>
        <Button onClick={submit} color="secondary" disabled={!target}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};